// ===================================
// ADMIN API SERVICE - STAGE 7 ENHANCED
// src/services/adminApi.js
// Extended with analytics, user details, session management
// ===================================

import api from './api';

// ===================================
// STATISTICS & ANALYTICS
// ===================================

export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

export const getAnalytics = async (params = {}) => {
  const response = await api.get('/admin/analytics', { params });
  return response.data;
};

// ===================================
// USER MANAGEMENT
// ===================================

export const getUsers = async (params = {}) => {
  const response = await api.get('/admin/users', { params });
  return response.data;
};

export const getUserDetails = async (userId) => {
  const response = await api.get(`/admin/users/${userId}`);
  return response.data;
};

export const updateUser = async (userId, data) => {
  const response = await api.put(`/admin/users/${userId}`, data);
  return response.data;
};

export const makeUserAdmin = async (userId) => {
  const response = await api.post(`/admin/users/${userId}/make-admin`);
  return response.data;
};

export const removeUserAdmin = async (userId) => {
  const response = await api.post(`/admin/users/${userId}/remove-admin`);
  return response.data;
};

export const resetUserPassword = async (userId, newPassword) => {
  const response = await api.post(`/admin/users/${userId}/reset-password`, { newPassword });
  return response.data;
};

// ===================================
// SESSION MANAGEMENT
// ===================================

export const getAdminSessions = async (params = {}) => {
  const response = await api.get('/admin/sessions', { params });
  return response.data;
};

export const getSessionDetails = async (sessionId) => {
  const response = await api.get(`/admin/sessions/${sessionId}`);
  return response.data;
};

export const updateSession = async (sessionId, data) => {
  const response = await api.put(`/admin/sessions/${sessionId}`, data);
  return response.data;
};

// ===================================
// REFUND MANAGEMENT
// ===================================

export const getRefundRequests = async (status = 'pending') => {
  const response = await api.get('/admin/refund-requests', { params: { status } });
  return response.data;
};

export const approveRefund = async (id, notes) => {
  const response = await api.post(`/admin/refund-requests/${id}/approve`, { notes });
  return response.data;
};

export const rejectRefund = async (id, notes) => {
  const response = await api.post(`/admin/refund-requests/${id}/reject`, { notes });
  return response.data;
};

// ===================================
// SETTINGS
// ===================================

export const getSettings = async () => {
  const response = await api.get('/admin/settings');
  return response.data;
};

export const updateSetting = async (key, value) => {
  const response = await api.put(`/admin/settings/${key}`, { value });
  return response.data;
};

// ===================================
// SALESFORCE
// ===================================

export const getSalesforceStatus = async () => {
  const response = await api.get('/admin/salesforce/status');
  return response.data;
};

export const getSalesforceConfig = async () => {
  const response = await api.get('/admin/salesforce/config');
  return response.data;
};

export const testSalesforceAuth = async () => {
  const response = await api.post('/admin/salesforce/test-auth');
  return response.data;
};

export const getSyncStatus = async () => {
  const response = await api.get('/admin/salesforce/sync-status');
  return response.data;
};

export const syncAllUsers = async (limit = 100) => {
  const response = await api.post('/admin/salesforce/sync-all-users', { limit });
  return response.data;
};

export const syncAllSessions = async (limit = 100) => {
  const response = await api.post('/admin/salesforce/sync-all-sessions', { limit });
  return response.data;
};

export const syncUser = async (userId) => {
  const response = await api.post(`/admin/salesforce/sync-user/${userId}`);
  return response.data;
};

export const syncSession = async (sessionId) => {
  const response = await api.post(`/admin/salesforce/sync-session/${sessionId}`);
  return response.data;
};

/**
 * Get all feedback (paginated, with filters)
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page (default 50)
 * @param {number} params.offset - Pagination offset
 * @param {number} params.rating - Filter by rating (1-5)
 * @param {string} params.source - Filter by source (email, dashboard, app, api)
 * @param {boolean} params.wouldRecommend - Filter by recommendation
 * @param {string} params.sortBy - Sort field (created_at, rating, overall_score)
 * @param {string} params.sortOrder - ASC or DESC
 */
export const getFeedback = async (params = {}) => {
  const response = await api.get('/feedback/admin/all', { params });
  return response.data;
};

/**
 * Get feedback statistics
 * @param {number} days - Number of days to include in stats (default 30)
 */
export const getFeedbackStats = async (days = 30) => {
  const response = await api.get('/feedback/admin/stats', { params: { days } });
  return response.data;
};

/**
 * Get single feedback detail
 * @param {string} feedbackId - Feedback UUID
 */
export const getFeedbackDetail = async (feedbackId) => {
  const response = await api.get(`/feedback/admin/${feedbackId}`);
  return response.data;
};

/**
 * Delete feedback (admin only)
 * @param {string} feedbackId - Feedback UUID
 */
export const deleteFeedback = async (feedbackId) => {
  const response = await api.delete(`/feedback/admin/${feedbackId}`);
  return response.data;
};
// Default export for backwards compatibility
export default {
  getAdminStats,
  getAnalytics,
  getUsers,
  getUserDetails,
  updateUser,
  makeUserAdmin,
  removeUserAdmin,
  resetUserPassword,
  getAdminSessions,
  getSessionDetails,
  updateSession,
  getRefundRequests,
  approveRefund,
  rejectRefund,
  getSettings,
  updateSetting,
  getSalesforceStatus,
  getSalesforceConfig,
  testSalesforceAuth,
  getSyncStatus,
  syncAllUsers,
  syncAllSessions,
  syncUser,
  syncSession,
    getFeedback,
  getFeedbackStats,
  getFeedbackDetail,
  deleteFeedback,
};